/**
 * McpTokenScopes — E4 · editar permisos de un token EXISTENTE.
 * El token NO se regenera: las conexiones MCP ya configuradas siguen
 * funcionando y el cambio aplica en la llamada siguiente.
 */
import { useState } from 'react';
import { ShieldAlert, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { mcpService } from '../../services/mcpService';

const GROUPS = [
  ['Proyectos', ['projects:read', 'projects:write']],
  ['Assets', ['assets:read', 'assets:write']],
  ['Workflows', ['workflows:read', 'workflows:write', 'workflows:execute']],
  ['Timeline', ['timeline:read', 'timeline:write', 'timeline:render']],
  ['Plantillas', ['templates:read', 'templates:write']],
];

const McpTokenScopes = ({ token, catalog, onClose, onSaved }) => {
  const [picked, setPicked] = useState(new Set(token.scopes || []));
  const [saving, setSaving] = useState(false);
  const sensitive = new Set(catalog?.sensitive_scopes || []);
  const all = catalog?.all_scopes || [];

  const toggle = (scope) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(scope)) next.delete(scope); else next.add(scope);
    return next;
  });

  const save = async () => {
    const scopes = [...picked];
    const added = scopes.filter((s) => !(token.scopes || []).includes(s));
    const addedSensitive = added.filter((s) => sensitive.has(s));
    if (addedSensitive.length && !window.confirm(
      'Este token va a poder modificar timelines y gastar créditos en renders.\n\n'
      + `Permisos que estás agregando: ${addedSensitive.join(', ')}\n\n¿Confirmás?`
    )) return;
    setSaving(true);
    try {
      const res = await mcpService.updateTokenScopes(token.id, scopes);
      toast.success(`Permisos actualizados (+${res.added.length} / -${res.removed.length})`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail
        || 'No se pudieron actualizar los permisos');
    } finally { setSaving(false); }
  };

  return (
    <div className="mcp-scope-editor" data-testid={`mcp-scope-editor-${token.id}`}>
      <div className="mcp-scope-editor-head">
        <strong>Permisos de “{token.name}”</strong>
        <span>El token no cambia: no hace falta reconectar el MCP.</span>
      </div>

      {GROUPS.map(([label, scopes]) => {
        const available = scopes.filter((s) => all.includes(s));
        if (!available.length) return null;
        return (
          <div className="mcp-scope-group" key={label}>
            <em>{label}</em>
            {available.map((scope) => (
              <label
                key={scope}
                className={`mcp-scope-row ${sensitive.has(scope) ? 'sensitive' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={picked.has(scope)}
                  onChange={() => toggle(scope)}
                  data-testid={`mcp-scope-${token.id}-${scope.replace(':', '-')}`}
                />
                <code>{scope}</code>
                {sensitive.has(scope) && <ShieldAlert size={11} />}
              </label>
            ))}
          </div>
        );
      })}

      <div className="mcp-scope-editor-actions">
        <button onClick={save} disabled={saving} data-testid={`mcp-scope-save-${token.id}`}>
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
          Guardar permisos
        </button>
        <button className="ghost" onClick={onClose} data-testid={`mcp-scope-cancel-${token.id}`}>
          <X size={11} /> Cancelar
        </button>
      </div>
    </div>
  );
};

export default McpTokenScopes;
